/*=========================================================================================
  File Name: main.js
  Description: main vue(js) file
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from "vue";
import App from "./App.vue";
import lodashHelper from './helper/lodashUtility';
import { format } from "date-fns";

// Vuesax Component Framework
import Vuesax from "vuesax";
import "material-icons/iconfont/material-icons.css"; //Material Icons
import "vuesax/dist/vuesax.css"; // Vuesax
Vue.use(Vuesax);


// axios
import axios from "./axios.js";
Vue.prototype.$http = axios;
// API Calls
import "./http/requests";

// Theme Configurations
import "../themeConfig.js";

// Globally Registered Components
import "./globalComponents.js";

// Styles: SCSS
import "./assets/scss/main.scss";

// Tailwind
import "@/assets/css/main.css";

// Vue Router
import router from "./router";

// Vuex Store
import store from "./store/store";

// i18n
import i18n from "./i18n/i18n";

// Vuexy Admin Filters
import "./filters/filters";

import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";

Vue.component("v-select", vSelect);
import VueScrollTo from "vue-scrollto";
Vue.use(VueScrollTo);

// VeeValidate
import VeeValidate, { Validator } from "vee-validate";
VeeValidate.setMode("eager");

VeeValidate.Validator.localize("en", {
  custom: {
    password: {
      regex:
        "Password must contain at least 8 characters including one uppercase letter,one lowercase letter and one number.",
    },
  },
});

VeeValidate.Validator.extend("decimalValidation", {
  getMessage: (field) => `The ${field} should be greater than 0.01.`,
  validate: (value) => parseFloat(value) > parseFloat(0.01),
});

Vue.use(VeeValidate, {
  classes: true,
});

// Vuejs - Vue wrapper for hammerjs
import { VueHammer } from "vue2-hammer";
Vue.use(VueHammer);

import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";

Vue.use(Toast, {
  transition: "Vue-Toastification__bounce",
  maxToasts: 30,
  newestOnTop: true,
  filterBeforeCreate: (toast, toasts) => {
    if (toasts.filter(
      t => t.message === toast.message
    ).length !== 0) {
      // Returning false discards the toast
      return false;
    }
    // You can modify the toast if you want
    return toast;
  }
});

// PrismJS
import "prismjs";
import "prismjs/themes/prism-tomorrow.css";

// custom directive to make button rounded
Vue.directive("round", {
  bind(el, binding, vnode) {
    el.style.borderRadius = "30px";
  },
});

// Sentry logging
import * as Sentry from "@sentry/vue";
import { BrowserTracing } from "@sentry/tracing";

Sentry.init({
  Vue,
  dsn: process.env.VUE_APP_SENTRY_DSN,
  environment: process.env.VUE_APP_SENTRY_ENV,
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracingOrigins: [process.env.VUE_APP_SENTRY_ORIGINS, /^\//],
    }),
  ],
  tracesSampleRate: parseFloat(process.env.VUE_APP_SENTRY_RATE),
  ignoreErrors: [
    'Error',
    /^401$/,
    /^410$/,
  ],
});

VeeValidate.Validator.extend("phoneFormat", {
  getMessage: () => "The phone number format is invalid.",
  validate: (value) =>
    value.match(
      /^(^04\(?\d{2}\)?[\s.-]\d{3}[\s.-]\d{3})|(^1300[\s.-]\d{3}[\s.-]\d{3})|(^1800[\s.-]\d{3}[\s.-]\d{3})|(^13[\s.-]\d{2}[\s.-]\d{2}$)|(^02[\s.-]\d{4}[\s.-]\d{4})|(^03[\s.-]\d{4}[\s.-]\d{4})|(^07[\s.-]\d{4}[\s.-]\d{4})|(^08[\s.-]\d{4}[\s.-]\d{4})/
    ) !== null,
});

VeeValidate.Validator.extend("urlFormat", {
  getMessage: () => "The website field is not a valid url.",
  validate: (value) =>
    value.match(
      /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi
    ) !== null,
});

const lodashHelperPlugin = {
  install () {
    Vue.lodashHelper = lodashHelper
    Vue.prototype.$lodashHelper = lodashHelper
  }
}

Vue.use(lodashHelperPlugin)

// formats currency as $123,56.67
Vue.mixin({
  methods: {
    formatDateOrder(value) {
      return format(new Date(value), "dd/MM/yyyy");
    },
    auto_grow(elem) {
      let element = document.getElementById(elem);
      element.style.height = "5px";
      element.style.height = element.scrollHeight + "px";
    },
    replaceLegalContent(str, mix) {
      const RGX = /{{(.*?)}}/g;
      return str.replace(RGX, (x, key, y) => {
        x = 0;
        y = mix;
        key = key.trim().split(".");
        while (y && x < key.length) {
          y = y[key[x++]];
        }
        return y != null ? y : "";
      });
    },

    getPlanName(val) {
      return val.paymentRequest.paylaterPlans.length == 0 ? "" : "test";
    },
    formatCurrency(value) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value);
    },

    maskBsb(string) {
      if (!string.includes("-")) {
        let arr = string.replace(" ", "").split("");
        arr.splice(3, 0, "-");
        string = arr.join("");
      }

      return string;
    },

    showToastMessage(title = "Success", message = "Success", type = "success") {
      let toastDetail = {};
      const icon = {
        iconClass: "",
        iconChildren: "",
        iconTag: "span"
      };
      toastDetail.position = "bottom-left";
      toastDetail.closeOnClick = false;
      toastDetail.pauseOnFocusLoss = false;
      toastDetail.pauseOnHover = false;
      toastDetail.draggable = false;
      toastDetail.draggablePercent = 0.1;
      toastDetail.showCloseButtonOnHover = false;
      toastDetail.hideProgressBar = true;
      toastDetail.closeButton = "button";
      toastDetail.icon = icon;
      toastDetail.rtl = false;
      toastDetail.message = message;

      if (type == "success") {
        toastDetail.icon.iconClass = "material-icons text-success";
        toastDetail.icon.iconChildren = "check_circle_outline";
        this.$toast.success(<div class="container"><h3>{title}</h3><p class="empty:hidden">{message}</p></div>, toastDetail);
      } else if (type == "warning") {
        toastDetail.timeout = false;
        toastDetail.icon.iconClass = "material-icons text-warning";
        toastDetail.icon.iconChildren = "information_outline";
        this.$toast.warning(<div class="container"><h3>{title}</h3><p class="empty:hidden">{message}</p></div>, toastDetail);
      } else {
        toastDetail.timeout = false;
        toastDetail.icon.iconClass = "material-icons text-danger";
        toastDetail.icon.iconChildren = "highlight_off";
        this.$toast.error(<div class="container"><h3>{title}</h3><p class="empty:hidden">{message}</p></div>, toastDetail);
      }
    },
  },
  computed: {
    isFormDirty() {
      return Object.keys(this.fields).some((key) => this.fields[key].dirty);
    },
    validateForm() {
      return !this.errors.any();
    },
    maxCardMsf() {
      return 1.75;
    }
  },
});

// Feather font icon
require("./assets/css/iconfont.css");

// Vue select css
// Note: In latest version you have to add it separately

Vue.config.productionTip = false;
const origin = console.error;
console.error = (error) => {

  if ((/Loading chunk [a-zA-z0-9]/.test(error.message) && /failed/.test(error.message)) || (/Loading CSS chunk [a-zA-z0-9]/.test(error.message) && /failed/.test(error.message))) {
    window.location.reload();
  }
}

new Vue({
  router,
  store,
  i18n,
  watch: {
    $route: {
      handler(to, from){
        if ( to.name !== from.name ) {
          if ( localStorage.getItem('formFieldUpdated') && localStorage.getItem('formFieldUpdated') === 'yes' ) {
            localStorage.setItem('formFieldUpdated', 'no')
          }

          if ( localStorage.getItem('redirectTo') && localStorage.getItem('redirectTo') !== 'admin-login') {
            localStorage.setItem('redirectTo', undefined)
          }
        }
      },
      flush: 'post',
    }
  },
  render: (h) => h(App),
}).$mount("#app");
